import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import './AgentInfo.css';

const AgentInfo = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = '';
    
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.target.classList.toggle('visible', e.isIntersecting)),
      { threshold: 0.15 }
    );
    document.querySelectorAll('.animate-in').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`agent-info-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <Helmet>
        <title>About David Hamady - Real Estate Agent in Flint, MI</title>
        <meta name="description" content="Meet David Hamady, your trusted real estate agent in Flint and Genesee County. Expert guidance for buying, selling, and investing in single family, multi-family, and commercial properties." />
      </Helmet>
      
      <div className="agent-info-container">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Home
        </button>
        
        <div className="agent-profile-section animate-in">
          <div className="agent-photo">
            <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/headshots/david.hamady.flint_62e436a10ae14e62bcdca74224f02663.png" alt={SITE_CONFIG.AGENT.NAME} />
          </div>
          
          <div className="agent-details">
            <h1>David Hamady</h1>
            <p className="agent-title">REALTOR®</p>
            <p className="agent-location">Flint, Michigan</p>
            
            <div className="contact-info">
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <a href="mailto:david@granthamadyrealtors.com">david@granthamadyrealtors.com</a>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <a href="tel:(810) 814-0490">(810) 814-0490</a>
              </div>
              <div className="contact-item">
                <span className="contact-icon">🏢</span>
                <span>License: 6501274903 | HamadyRobinson, REALTORS®</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="agent-bio-section">
          <h2 className="animate-in">About Me</h2>
          <div className="bio-content">
            <h3 className="animate-in">Real Estate Expertise Grounded in Law, Economics, and Finance</h3>
            <p className="animate-in">
              A real estate transaction is often the largest financial event of your life. Whether you are buying your first home, securing an investment property, or selling a longtime family residence, you deserve more than just a salesperson—you deserve a strategic advocate.
            </p>
            <p className="animate-in">
              David Hamady brings a rare, multi-disciplinary approach to the Mid-Michigan real estate market. With a Law Degree from Michigan State University and a Bachelor's in Economics &amp; Management from Albion College, David views every deal through a lens of risk management, market trends, and high-level negotiation.
            </p>

            <h3 className="animate-in">A Comprehensive Financial Perspective</h3>
            <p className="animate-in">Before transitioning into real estate full-time, David built an extensive career in the financial sector:</p>

            <div className="credential-cards">
              <div className="credential-card animate-in" style={{transitionDelay: '0ms'}}>
                <span className="credential-icon">📈</span>
                <div>
                  <strong>Investment Adviser</strong>
                  <p>Deep understanding of real estate as a wealth-building asset.</p>
                </div>
              </div>
              <div className="credential-card animate-in" style={{transitionDelay: '100ms'}}>
                <span className="credential-icon">🏦</span>
                <div>
                  <strong>Mortgage Banker</strong>
                  <p>Expert navigation of lending complexities and interest rate environments.</p>
                </div>
              </div>
              <div className="credential-card animate-in" style={{transitionDelay: '200ms'}}>
                <span className="credential-icon">💳</span>
                <div>
                  <strong>Credit Repair Specialist</strong>
                  <p>Unique solutions for buyers who need a roadmap to qualification.</p>
                </div>
              </div>
            </div>

            <p className="animate-in">
              David's philosophy is simple: He doesn't walk away. If your credit isn't ready or the timing isn't right, he will stay by your side, providing the guidance and FICO scoring insight needed to get you to the closing table.
            </p>

            <h3 className="animate-in">Deep Roots in Mid-Michigan</h3>
            <p className="animate-in">
              Serving Flint, Grand Blanc, Burton, and the surrounding areas, David is deeply familiar with the local neighborhoods and property values. His commitment to the "Weichert Difference" means you can expect a higher level of responsive, knowledgeable service that is tailored to your specific goals.
            </p>
            <p className="closing-statement animate-in">
              When you work with David, you aren't just getting a listing on the MLS—you're getting a professional dedicated to protecting your interests and maximizing your investment.
            </p>
          </div>
        </div>
        
        <div className="cta-section animate-in">
          <h2>Ready to Work Together?</h2>
          <p>Let's discuss your real estate goals and find the perfect property for you.</p>
          <div className="cta-buttons">
            <a href="mailto:david@granthamadyrealtors.com?subject=Real Estate Inquiry" className="cta-button primary">
              Email Me
            </a>
            <button onClick={() => { navigate('/'); setTimeout(() => { window.dispatchEvent(new CustomEvent('openGlobeMap')); }, 100); }} className="cta-button secondary">
              View Properties
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentInfo;
